import type { Card, CardState, MemoryGame } from "./types.js";

const EMOJIS = [
  "🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼",
  "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔",
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function createGame(pairCount: number = 8): MemoryGame {
  const selected = EMOJIS.slice(0, Math.min(pairCount, EMOJIS.length));
  const pairs = shuffle([...selected, ...selected]);
  const cards: Card[] = pairs.map((emoji, i) => ({
    id: i,
    emoji,
    state: "hidden" as CardState,
  }));
  return {
    cards,
    flipped: [],
    moves: 0,
    matchedPairs: 0,
    totalPairs: selected.length,
    isComplete: false,
    waiting: false,
  };
}

export function flipCard(
  game: MemoryGame,
  index: number
): { ok: true; game: MemoryGame } | { ok: false; reason: string } {
  if (game.waiting) return { ok: false, reason: "Wait for cards to flip back." };
  if (game.isComplete) return { ok: false, reason: "Game is over." };
  if (index < 0 || index >= game.cards.length) return { ok: false, reason: "Invalid card." };
  if (game.cards[index].state !== "hidden") return { ok: false, reason: "Card already revealed." };
  if (game.flipped.length >= 2) return { ok: false, reason: "Already two cards flipped." };

  const cards = game.cards.map((c, i) =>
    i === index ? { ...c, state: "revealed" as CardState } : c
  );
  const flipped = [...game.flipped, index];
  const moves = flipped.length === 2 ? game.moves + 1 : game.moves;

  return {
    ok: true,
    game: { ...game, cards, flipped, moves, waiting: flipped.length === 2 },
  };
}

export function checkMatch(game: MemoryGame): MemoryGame {
  if (game.flipped.length !== 2) return game;
  const [a, b] = game.flipped;
  const cards = [...game.cards];

  if (cards[a].emoji === cards[b].emoji) {
    cards[a] = { ...cards[a], state: "matched" };
    cards[b] = { ...cards[b], state: "matched" };
    const matchedPairs = game.matchedPairs + 1;
    return {
      ...game,
      cards,
      flipped: [],
      waiting: false,
      matchedPairs,
      isComplete: matchedPairs === game.totalPairs,
    };
  }

  cards[a] = { ...cards[a], state: "hidden" };
  cards[b] = { ...cards[b], state: "hidden" };
  return { ...game, cards, flipped: [], waiting: false };
}
